import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase credentials not found!')
  console.error('Please check your .env file and ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set')
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      headers: {
        'x-application-name': 'cropfield-intranet',
      },
    },
  }
)

// Database helper functions for backwards compatibility
export const db = {
  // Run a query (INSERT, UPDATE, DELETE)
  async run(query: string, params: any[] = []) {
    // This function is a placeholder. Direct SQL execution via RPC is generally not recommended
    // for general use due to security and performance implications.
    // For specific use cases, ensure your `execute_sql` RPC is secure and optimized.
    const { data, error } = await supabase.rpc('execute_sql', {
      query,
      params
    });
    if (error) throw error;
    return data;
  },

  // Get single row
  async get(table: string, query: any = {}) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .match(query)
      .single();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found
    return data;
  },

  // Get multiple rows
  async all(table: string, query: any = {}) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .match(query);
    if (error) throw error;
    return data || [];
  },

  // Insert new record
  async insert(table: string, data: any) {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select()
      .single();
    if (error) throw error;
    return result;
  },

  // Update record
  async update(table: string, id: string, data: any) {
    const { data: result, error } = await supabase
      .from(table)
      .update(data)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return result;
  },

  // Delete record
  async delete(table: string, id: string) {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  }
};
export type Database = {
  public: {
    Tables: {
      usuarios: {
        Row: {
          id: string
          auth_id: string | null
          usuario: string
          nome: string
          email: string
          setor: string
          role: string
          ativo: boolean
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          auth_id?: string | null
          usuario: string
          nome: string
          email: string
          setor?: string
          role?: string
          ativo?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          auth_id?: string | null
          usuario?: string
          nome?: string
          email?: string
          setor?: string
          role?: string
          ativo?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
      }
      mural_posts: {
        Row: {
          id: string
          usuario_id: string
          autor_usuario: string
          autor_setor: string
          titulo: string
          conteudo: string
          publicado: boolean
          pinned: boolean
          likes_count: number
          comments_count: number
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          usuario_id: string
          autor_usuario: string
          autor_setor: string
          titulo: string
          conteudo: string
          publicado?: boolean
          pinned?: boolean
          likes_count?: number
          comments_count?: number
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          usuario_id?: string
          autor_usuario?: string
          autor_setor?: string
          titulo?: string
          conteudo?: string
          publicado?: boolean
          pinned?: boolean
          likes_count?: number
          comments_count?: number
          created_at?: string | null
          updated_at?: string | null
        }
      }
      reservas_salas: {
        Row: {
          id: string
          usuario_id: string
          sala: string
          data_reserva: string
          hora_inicio: string
          hora_fim: string
          motivo: string
          observacoes: string | null
          status: string
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          usuario_id: string
          sala: string
          data_reserva: string
          hora_inicio: string
          hora_fim: string
          motivo: string
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          usuario_id?: string
          sala?: string
          data_reserva?: string
          hora_inicio?: string
          hora_fim?: string
          motivo?: string
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
        }
      }
      trocas_proteina: {
        Row: {
          id: string
          usuario_id: string
          data_troca: string
          proteina_original: string
          proteina_nova: string
          refeicao: string | null
          observacoes: string | null
          status: string
          created_at: string | null
          updated_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          usuario_id: string
          data_troca: string
          proteina_original: string
          proteina_nova: string
          refeicao?: string | null
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          usuario_id?: string
          data_troca?: string
          proteina_original?: string
          proteina_nova?: string
          refeicao?: string | null
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
      }
      ti_solicitacoes: {
        Row: {
          id: string
          usuario_id: string
          solicitante_nome: string
          solicitante_email: string | null
          equipamento: string
          descricao: string
          prioridade: string
          status: string
          observacoes_ti: string | null
          created_at: string | null
          updated_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          usuario_id: string
          solicitante_nome: string
          solicitante_email?: string | null
          equipamento: string
          descricao: string
          prioridade: string
          status?: string
          observacoes_ti?: string | null
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          usuario_id?: string
          solicitante_nome?: string
          solicitante_email?: string | null
          equipamento?: string
          descricao?: string
          prioridade?: string
          status?: string
          observacoes_ti?: string | null
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
      }
      portaria_agendamentos: {
        Row: {
          id: string
          nome_visitante: string
          documento: string
          empresa: string | null
          data_visita: string
          hora_entrada: string
          hora_saida: string | null
          responsavel_id: string
          setor_destino: string
          motivo: string
          observacoes: string | null
          status: string
          created_at: string | null
          updated_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          nome_visitante: string
          documento: string
          empresa?: string | null
          data_visita: string
          hora_entrada: string
          hora_saida?: string | null
          responsavel_id: string
          setor_destino: string
          motivo: string
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          nome_visitante?: string
          documento?: string
          empresa?: string | null
          data_visita?: string
          hora_entrada?: string
          hora_saida?: string | null
          responsavel_id?: string
          setor_destino?: string
          motivo?: string
          observacoes?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
          updated_at?: string | null
        }
      }
      pontos: {
        Row: {
          id: string
          usuario_id: string
          acao: string
          pontos: number
          descricao: string | null
          metadata: any | null
          created_at: string | null
        }
        Insert: {
          id?: string
          usuario_id: string
          acao: string
          pontos: number
          descricao?: string | null
          metadata?: any | null
          created_at?: string | null
        }
        Update: {
          id?: string
          usuario_id?: string
          acao?: string
          pontos?: number
          descricao?: string | null
          metadata?: any | null
          created_at?: string | null
        }
      }
    }
  }
}